"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { inventoryService } from "../../../../services/inventoryService/index.js";
import { normalizeProductApi } from "../../../../services/productService/normalize.js";
import { normalizeProductSummaryApi } from "../../../../services/productService/normalizePCP/normalize.js";
import { normalizeProductSummaryApiV2 } from "../../../../services/productService/normalizePCP_V2/normalize.js";
import { normalizeLwData } from "../../../../services/searchService/LucidWorks/normalizeLwData.js";
import { isBD } from "../../../../utils/businessType.lib.js";
import { isFeatureEnabledForApplication } from "../../../../utils/contentstack/lib.js";
import { ProductTileProvider } from "../../../../utils/productTile/ProductTileProvider.js";
import { actionButtonControlsCheck } from "../../../../utils/productTile/utils.js";
import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Link } from "@nxt-costco-com/forge-components";
import { Analytics } from "../../../Analytics/index.js";
import { ONE, KEY_PRODUCT_CLICK, KEY_SEARCH_CLICK, CATEGORY_PAGE_TYPE } from "../../../Analytics/constants.js";
import "../../../Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../../Chrome/BrowseContext/hook.js";
import { Variants, COMPONENT_NAME } from "../constants.js";
import { useAuth } from "../../../../hooks/auth/useAuth/index.js";
import { CardBody } from "./CardBody/index.js";
import { pushAnalyticsDataEvent } from "./utils.js";
import { ProductTileSkeleton } from "./view.skeleton.js";
import { containerStyles, linkWrapperStyles } from "./styles.js";
const ProductTileUI = (props) => {
  var _a, _b;
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(
    false
  );
  const [normalizedData, setNormalizedData] = useState(null);
  const [pdpNavigationUrl, setPdpNavigationUrl] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [loginFunctionsLoaded, setLoginFunctionsLoaded] = useState(false);
  const { isUserSignedIn, isLoading: isAuthUtilsLoading } = useAuth();
  useEffect(() => {
    if (!isAuthUtilsLoading) {
      isUserSignedIn().then((result) => {
        setIsUserLoggedIn(result);
      }).finally(() => {
        setLoginFunctionsLoaded(true);
      });
    }
  }, [isAuthUtilsLoading, isUserSignedIn]);
  const cookieContext = useBrowseContext();
  const {
    addToCartAnalytics,
    configData,
    dataPrivate,
    hideReviewStarValue,
    inventoryAware,
    isCriteo = false,
    isHorizontal,
    locale,
    lwData,
    maxImageHeight,
    minProductTileImageWidth,
    maxProductTileImageWidth,
    productApiResponse,
    productTileMaxLineNumber,
    ratingVariant,
    showActionButton,
    showReviews = true,
    showSkeleton = false,
    sx,
    variant,
    pageType,
    isProductDetailsPage,
    enableAnalytics,
    isSponsored,
    allowBatchApi = true,
    product_tile_data_source
  } = props;
  const isInformative = variant === Variants.INFORMATIVE;
  const {
    featureToggleConfig,
    translationsConfig,
    inventoryApiConfig,
    fsaConfig
  } = configData || {};
  const enablePromoBreakdown = isFeatureEnabledForApplication(
    (_a = featureToggleConfig == null ? void 0 : featureToggleConfig.promoBreakdown) == null ? void 0 : _a.featureOn,
    (_b = featureToggleConfig == null ? void 0 : featureToggleConfig.promoBreakdown) == null ? void 0 : _b.enabledApplications
  );
  const { inventoryItemNumbers, productApiResponseData } = productApiResponse || {};
  const stringifiedInventoryItemNumbers = inventoryItemNumbers == null ? void 0 : inventoryItemNumbers.map(String);
  const { actionButtonControls } = translationsConfig || {};
  const { requiresDeliveryZipcode } = actionButtonControls || {};
  const { deliveryLocation, bdDeliveryLocation } = cookieContext;
  const isBusinessDeliverySite = isBD() ?? false;
  const isMeetingActionButtonRequirements = showActionButton && actionButtonControlsCheck(
    requiresDeliveryZipcode,
    isBusinessDeliverySite,
    isBusinessDeliverySite ? bdDeliveryLocation : deliveryLocation
  );
  useEffect(() => {
    var _a2, _b2, _c;
    const abortController = new AbortController();
    const signal = abortController.signal;
    const callProductSummaryAPI = ((_b2 = (_a2 = configData == null ? void 0 : configData.featureToggleConfig) == null ? void 0 : _a2.callProductSummaryAPI) == null ? void 0 : _b2.isEnabled) || product_tile_data_source === "product_summary_api";
    const fetchParsedProductData = async () => {
      var _a3, _b3;
      let inventoryResponseData = productApiResponse == null ? void 0 : productApiResponse.inventoryResponseData;
      if (inventoryAware && allowBatchApi && !(productApiResponse == null ? void 0 : productApiResponse.hasBatchInventoryResponseData)) {
        const inventoryResponse = await inventoryService(
          stringifiedInventoryItemNumbers,
          inventoryApiConfig,
          signal,
          cookieContext
        );
        if (!(inventoryResponse == null ? void 0 : inventoryResponse.ok)) {
          console.error("Inventory Api failed", inventoryResponse);
        } else {
          inventoryResponseData = await inventoryResponse.json();
        }
      }
      const { warehousePriceOnline } = (configData == null ? void 0 : configData.featureToggleConfig) || {};
      let normalizedDataObj;
      if (callProductSummaryAPI && (warehousePriceOnline == null ? void 0 : warehousePriceOnline.isEnabled)) {
        normalizedDataObj = normalizeProductSummaryApiV2(
          {
            inventoryResponseData,
            productApiResponseData
          },
          configData,
          Boolean(isUserLoggedIn),
          cookieContext,
          null,
          isSponsored,
          isProductDetailsPage,
          locale
        );
      } else {
        const normalizeFunc = callProductSummaryAPI ? normalizeProductSummaryApi : normalizeProductApi;
        normalizedDataObj = normalizeFunc(
          {
            inventoryResponseData,
            productApiResponseData
          },
          configData,
          Boolean(isUserLoggedIn),
          cookieContext,
          null,
          isSponsored,
          isProductDetailsPage
        );
      }
      setNormalizedData(
        callProductSummaryAPI ? normalizedDataObj : normalizedDataObj == null ? void 0 : normalizedDataObj[0]
      );
      setPdpNavigationUrl(
        callProductSummaryAPI ? (_a3 = normalizedDataObj == null ? void 0 : normalizedDataObj.productInfoData) == null ? void 0 : _a3.productLinkUrl : (_b3 = normalizedDataObj == null ? void 0 : normalizedDataObj[0]) == null ? void 0 : _b3.productInfoData.productLinkUrl
      );
      setIsLoading(false);
    };
    if (!normalizedData && loginFunctionsLoaded) {
      if (!lwData) {
        fetchParsedProductData();
      } else {
        const lwNormalizedDataObj = (_c = normalizeLwData(
          [lwData],
          configData,
          Boolean(isUserLoggedIn),
          cookieContext
        )) == null ? void 0 : _c[0];
        setNormalizedData(lwNormalizedDataObj);
        setPdpNavigationUrl(
          lwNormalizedDataObj == null ? void 0 : lwNormalizedDataObj.productInfoData.productLinkUrl
        );
        setIsLoading(false);
      }
    }
    return () => {
      abortController.abort();
    };
  }, [
    inventoryAware,
    configData,
    inventoryApiConfig,
    stringifiedInventoryItemNumbers,
    productApiResponseData,
    isUserLoggedIn,
    cookieContext,
    lwData,
    normalizedData,
    loginFunctionsLoaded,
    isSponsored,
    allowBatchApi,
    isProductDetailsPage,
    productApiResponse == null ? void 0 : productApiResponse.inventoryResponseData
  ]);
  if (showSkeleton || isLoading)
    return /* @__PURE__ */ jsx(
      ProductTileSkeleton,
      {
        isHorizontal,
        minProductTileImageWidth
      }
    );
  if (!normalizedData || !(normalizedData == null ? void 0 : normalizedData.isPublished)) return null;
  const { productInfoData, isFsaEligible, isChdiEligible } = normalizedData;
  const productTitleId = `${COMPONENT_NAME}_${productInfoData.partNumber}_title`;
  const tileContainerStyles = { ...containerStyles, ...sx };
  const handleClickAnalytics = () => {
    if (productApiResponse == null ? void 0 : productApiResponse.adobeAnalytics) {
      pushAnalyticsDataEvent({ ...productApiResponse == null ? void 0 : productApiResponse.adobeAnalytics });
    }
  };
  const doNotTrack = dataPrivate || isFsaEligible || isChdiEligible || !enableAnalytics;
  const analyticDataForLink = isProductDetailsPage ? {
    adItem: { url: pdpNavigationUrl },
    doNotTrack
  } : {
    doNotTrack: !enableAnalytics,
    trackedData: [
      {
        key: pageType === CATEGORY_PAGE_TYPE ? KEY_PRODUCT_CLICK : KEY_SEARCH_CLICK,
        value: ONE
      }
    ]
  };
  return /* @__PURE__ */ jsx(
    ProductTileProvider,
    {
      value: {
        addToCartAnalytics,
        isInformative,
        isSignedIn: isUserLoggedIn,
        locale,
        normalizedData,
        productTitleId,
        translationsConfig
      },
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          sx: [
            ...Array.isArray(tileContainerStyles) ? tileContainerStyles : [tileContainerStyles]
          ],
          "data-testid": `${COMPONENT_NAME}_${productInfoData.partNumber}`,
          "data-private": doNotTrack,
          role: "group",
          children: [
            /* @__PURE__ */ jsx(Analytics, { analyticData: analyticDataForLink, children: /* @__PURE__ */ jsx(
              Link,
              {
                href: pdpNavigationUrl || "",
                onClick: handleClickAnalytics,
                sx: linkWrapperStyles,
                children: /* @__PURE__ */ jsx(Box, { component: "span", sx: visuallyHidden, children: productInfoData.productName })
              }
            ) }),
            /* @__PURE__ */ jsx(
              CardBody,
              {
                fsaConfig,
                hideReviewStarValue,
                isCriteo,
                isHorizontal,
                maxImageHeight,
                minProductTileImageWidth,
                maxProductTileImageWidth,
                pdpNavigationUrl,
                productTileMaxLineNumber,
                ratingVariant,
                setPdpNavigationUrl,
                showActionButton: isMeetingActionButtonRequirements,
                showReviews: Boolean(showReviews),
                enablePromoBreakdown
              }
            )
          ]
        }
      )
    }
  );
};
ProductTileUI.displayName = COMPONENT_NAME;
export {
  ProductTileUI
};
